import SEOHead from '../components/SEOHead';
import BreadcrumbSchema from '../components/BreadcrumbSchema';

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24" aria-labelledby={`${id}-h`}>
    <h2 id={`${id}-h`} className="text-xl text-[#D4AF37] mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{title}</h2>
    <div className="text-white/60 text-sm leading-relaxed font-light space-y-3">{children}</div>
  </section>
);

function ShippingHardcoded() {
  return (
    <div data-testid="shipping-page" className="min-h-screen">
      <SEOHead
        title="Shipping Policy"
        description="JS Fragrance Shipping Policy — coverage, fees, processing time, courier partners, COD availability, and tracking. Nationwide delivery within the Philippines."
        url="https://jsfragrances.com/shipping"
      />
      <BreadcrumbSchema crumbs={[{ name: 'Home', path: '/' }, { name: 'Shipping Policy', path: '/shipping' }]} />

      <div className="bg-[#0A0A0A] py-12 sm:py-16 border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2 font-sans">Legal</p>
          <h1 className="text-4xl sm:text-5xl tracking-tight leading-none font-light text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Shipping Policy
          </h1>
          <p className="text-white/40 text-sm mt-3 font-sans">Last updated: February 1, 2026</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16 space-y-10">
        <Section id="coverage" title="Coverage">
          <p>
            JS Fragrance ships to <strong className="text-white/80">all provinces and regions within the Philippines</strong>.
            International shipping is on our roadmap and not yet available — for overseas Filipinos, we recommend asking a family
            member to receive your order locally and forward via your preferred balikbayan box service.
          </p>
        </Section>

        <Section id="processing" title="Order Processing">
          <p>
            Most orders are packed and dispatched within <strong className="text-white/80">1-2 business days</strong> after payment
            verification. Custom Scent Builder orders take 5-10 business days to blend before shipping. You&apos;ll receive a
            shipping confirmation email (and SMS, if you provided a number) with your tracking link.
          </p>
        </Section>

        <Section id="estimates" title="Estimated Delivery">
          <div className="space-y-3">
            <div className="border border-white/10 p-4 flex justify-between items-center">
              <div>
                <p className="text-white text-sm font-sans">Metro Manila</p>
                <p className="text-white/40 text-xs font-sans">NCR area</p>
              </div>
              <p className="text-[#D4AF37] text-sm font-sans font-medium">2-3 business days</p>
            </div>
            <div className="border border-white/10 p-4 flex justify-between items-center">
              <div>
                <p className="text-white text-sm font-sans">Luzon (outside NCR)</p>
                <p className="text-white/40 text-xs font-sans">Provinces</p>
              </div>
              <p className="text-[#D4AF37] text-sm font-sans font-medium">3-5 business days</p>
            </div>
            <div className="border border-white/10 p-4 flex justify-between items-center">
              <div>
                <p className="text-white text-sm font-sans">Visayas & Mindanao</p>
                <p className="text-white/40 text-xs font-sans">Island regions</p>
              </div>
              <p className="text-[#D4AF37] text-sm font-sans font-medium">5-7 business days</p>
            </div>
          </div>
          <p className="text-white/30 text-xs italic">
            Times are estimates only. Delivery may take longer during Philippine holidays, typhoons, courier strikes, or
            high-demand seasons (Valentine&apos;s, Christmas, big sales).
          </p>
        </Section>

        <Section id="fees" title="Shipping Fees">
          <p>
            Shipping fees are calculated at checkout based on your delivery region and order weight. Standard rates:
          </p>
          <ul className="space-y-1.5 ml-4">
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> Metro Manila &amp; Luzon — starting ₱60</li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> Visayas — starting ₱120</li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> Mindanao — starting ₱160</li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> Lalamove / Grab same-day (Metro Manila only) — at-cost based on courier rate</li>
          </ul>
          <p>
            <strong className="text-white/80">Free shipping</strong> is offered occasionally on orders above a threshold —
            promotions are announced via our homepage banner, social media, and email.
          </p>
        </Section>

        <Section id="couriers" title="Our Courier Partners">
          <p>We work with reputable Philippine couriers based on your destination and order size:</p>
          <ul className="space-y-1.5 ml-4">
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> J&amp;T Express (nationwide)</li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> LBC Express (nationwide, fastest for remote areas)</li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> Flash Express (Luzon &amp; Visayas)</li>
            <li className="flex gap-2"><span className="text-[#D4AF37] mt-1.5">◆</span> Lalamove / Grab Express (Metro Manila same-day, available on request)</li>
          </ul>
        </Section>

        <Section id="cod" title="Cash on Delivery (COD)">
          <p>
            COD is available in most areas nationwide. First-time COD orders may receive a quick verification call to confirm
            the address before dispatch. COD orders that are refused or repeatedly missed during delivery may incur a
            non-refundable processing fee for future orders.
          </p>
        </Section>

        <Section id="tester-kit-shipping" title="Tester Kit Shipping (Ambassadors)">
          <p>
            Your Brand Ambassador Tester Kit ships within <strong className="text-white/80">2-3 business days after admin approval
            of your payment</strong>. We use the same courier network listed above. You&apos;ll receive a tracking link by email
            and SMS. The tester kit is shipped <strong className="text-white/80">once per ambassador account</strong> and cannot
            be re-shipped after delivery has been confirmed by the courier.
          </p>
        </Section>

        <Section id="distributor-shipping" title="Distributor / Bulk Shipping (MOQ 100+)">
          <p>
            Distributor orders ship via freight or pallet courier depending on volume. <strong className="text-white/80">The
            distributor shoulders shipping fees by default</strong>, although super-bulk tiers may include shipping incentives
            at JS&apos;s discretion.
          </p>
          <p>
            Production lead time depends on order size — typically 7-14 business days from payment confirmation to dispatch
            for orders under 500 pcs, longer for larger batches. We communicate the ETA at the time of order acceptance,
            before payment is processed.
          </p>
        </Section>

        <Section id="ambassador-fulfillment" title="Ambassador Zero-Capital Fulfillment">
          <p>
            For ambassadors who don&apos;t want to keep stock at home, we support a zero-capital fulfillment flow:
          </p>
          <ol className="space-y-1.5 ml-4 list-decimal list-outside">
            <li>Your customer messages YOU and orders.</li>
            <li>You collect full payment from your customer first (your profit margin is secured up-front).</li>
            <li>You forward the order details to JS, paying us your ambassador rate.</li>
            <li>We ship directly to your customer using your name as the sender, OR ship to you for personal handover — your choice.</li>
          </ol>
          <p>
            This way you don&apos;t need to outlay capital or maintain inventory. If you prefer to keep stock for faster fulfillment,
            you can purchase ambassador-rate stock at any time.
          </p>
        </Section>

        <Section id="tracking" title="Order Tracking">
          <p>
            Once your order ships, we send your tracking number via email and SMS. You can also paste it into our
            <a href="/track-order" className="text-[#D4AF37] hover:underline"> Track Order page</a> for live status — or check
            directly on the courier&apos;s website.
          </p>
        </Section>

        <Section id="packaging" title="Packaging & Handling">
          <p>
            Every JS Fragrance bottle ships in our signature black-and-gold packaging — gift-ready, no extra charge. Each
            bottle is filled to spec as Eau de Parfum at <strong className="text-white/80">30% oil concentration (Extrait-level)</strong>,
            sealed, individually wrapped, and cushioned to prevent leaks or breakage in transit. If your package arrives crushed
            or visibly damaged, please photograph it before opening and contact us within 48 hours.
          </p>
        </Section>

        <Section id="failed-delivery" title="Failed Delivery & Re-shipment">
          <p>
            If a delivery fails because of an incomplete or incorrect address provided at checkout, the package is returned to us.
            Re-shipping fees may apply, calculated at the same regional rate. Please double-check your delivery details before
            placing the order.
          </p>
        </Section>

        <Section id="international" title="International Shipping">
          <p>
            Not currently offered. We&apos;ll announce when this changes. In the meantime, feel free to use a freight-forwarder
            service of your choice — many of our customers abroad use balikbayan box providers without issue.
          </p>
        </Section>

        <Section id="support" title="Shipping Questions?">
          <p>
            Reach out via the <a href="/about#contact" className="text-[#D4AF37] hover:underline">Contact form</a>, email
            <a href="mailto:support@jsfragrances.com" className="text-[#D4AF37] hover:underline"> support@jsfragrances.com</a>, or
            message us on Facebook.
          </p>
        </Section>
      </div>
    </div>
  );
}

export default function ShippingPage() {
  return <ShippingHardcoded />;
}
